// ── Pricing Formula types ──
/**
 * Evaluates a rate_table pricing formula against user-provided field values.
 * Returns the computed price in minor units (e.g. cents) and a human-readable breakdown.
 *
 * Formula: sum(baseFields) * (annualRatePercent / 100) * installmentMultiplier
 */
export function evaluateRateTable(formula, fieldValues) {
    const rawBaseSum = formula.baseFields.reduce((acc, key) => {
        const raw = Number(fieldValues[key]);
        return acc + (Number.isFinite(raw) ? raw : 0);
    }, 0);
    if (rawBaseSum <= 0) {
        return { error: 'Base amount must be greater than zero' };
    }
    const multiplier = formula.baseMultiplier && formula.baseMultiplier > 0
        ? formula.baseMultiplier
        : 1;
    const baseSum = rawBaseSum * multiplier;
    const profileValue = String(fieldValues[formula.profileField] ?? '');
    const rateEntry = formula.rates.find((r) => r.profileValue === profileValue);
    if (!rateEntry) {
        return { error: `Unknown profile: ${profileValue}` };
    }
    const annualAmount = baseSum * (rateEntry.annualRatePercent / 100);
    let installmentMultiplier = 1;
    let installmentLabel = '';
    if (formula.installmentField && formula.installmentTiers?.length) {
        const installmentCount = Number(fieldValues[formula.installmentField]);
        const tier = formula.installmentTiers.find((t) => t.installments === installmentCount);
        if (tier) {
            installmentMultiplier = tier.multiplier;
            installmentLabel = tier.label;
        }
    }
    const totalAmount = annualAmount * installmentMultiplier;
    const priceMinor = Math.round(totalAmount * 100);
    const fmt = (n) => n.toLocaleString('es-UY', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const breakdown = [
        { label: 'Tasa anual', value: `${rateEntry.annualRatePercent}%` },
        { label: 'Costo anual', value: `$${fmt(annualAmount)}` },
    ];
    if (installmentLabel) {
        const finLabel = installmentMultiplier === 1
            ? `${installmentLabel} (sin recargo)`
            : installmentLabel;
        breakdown.push({ label: 'Financiación', value: finLabel });
        if (installmentMultiplier !== 1) {
            breakdown.push({ label: 'Recargo', value: `${((installmentMultiplier - 1) * 100).toFixed(0)}%` });
        }
    }
    const resultLabel = formula.resultLabel ?? 'Total';
    breakdown.push({ label: resultLabel, value: `$${fmt(totalAmount)}` });
    return {
        priceMinor,
        displayTotal: `$${fmt(totalAmount)}`,
        breakdown,
    };
}
